import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  Input,
  Select,
  Checkbox,
  Radio,
  Tabs,
  Progress,
  Divider,
  Modal,
  Drawer,
  Alert,
  EmptyState,
  LoadingState,
  Skeleton,
  ErrorState,
  ReviseHeader,
  ReviseBottomNav,
  useToast,
} from './index';
import {
  Zap,
  Check,
  AlertCircle,
  Search,
  BookOpen,
  ArrowRight,
  Sliders,
  Sparkles,
} from 'lucide-react';

export const DesignSystemShowcase: React.FC<{ onBackToApp?: () => void }> = ({ onBackToApp }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('buttons');
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [inputError, setInputError] = useState('');
  const [selectVal, setSelectVal] = useState('practice');
  const [checkedState, setCheckedState] = useState(true);
  const [radioVal, setRadioVal] = useState('option-a');
  const [progressVal, setProgressVal] = useState(65);
  const [navTab, setNavTab] = useState('home');

  return (
    <div className="min-h-screen bg-[#F7FAFA] text-[#111111] pb-24">
      {/* Sample Header Shell */}
      <ReviseHeader
        user={{
          name: 'Demo Student',
          mobile: '9876543210',
          role: 'student',
        }}
        language="en"
        onLanguageToggle={(lang) => showToast(`Language toggled to ${lang.toUpperCase()}`, 'info')}
        activeNavTab={navTab}
        onNavTabChange={setNavTab}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
        {/* Banner */}
        <div className="bg-white rounded-3xl p-6 border border-[#E5E9E9] shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="primary" dot>
                Phase 1 Design System
              </Badge>
              <Badge variant="success">Component Library Ready</Badge>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111]">REVise UI Foundation</h1>
            <p className="text-xs sm:text-sm text-[#5F6368] mt-1">
              Clean, spacious, modern, light & minimal component design tokens for competitive exam revision.
            </p>
          </div>
          {onBackToApp && (
            <Button variant="outline" size="sm" onClick={onBackToApp}>
              Return to Main Application
            </Button>
          )}
        </div>

        {/* Section Tabs */}
        <Tabs
          tabs={[
            { id: 'buttons', label: 'Buttons & Badges', icon: <Sparkles className="w-4 h-4" /> },
            { id: 'inputs', label: 'Forms & Controls', icon: <Sliders className="w-4 h-4" /> },
            { id: 'cards', label: 'Cards & Containers', icon: <BookOpen className="w-4 h-4" /> },
            { id: 'overlays', label: 'Modals & Drawers', icon: <Zap className="w-4 h-4" /> },
            { id: 'states', label: 'Feedback & States', icon: <AlertCircle className="w-4 h-4" /> },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="pills"
        />

        {/* TAB 1: BUTTONS & BADGES */}
        {activeTab === 'buttons' && (
          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle>Button Variants</CardTitle>
                <CardDescription>Primary, secondary, outline, ghost, and danger buttons with micro-interactions.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Button variant="primary" onClick={() => showToast('Primary button clicked!')}>
                  Primary Button
                </Button>
                <Button variant="secondary" onClick={() => showToast('Secondary button clicked!', 'info')}>
                  Secondary Button
                </Button>
                <Button variant="outline" onClick={() => showToast('Outline button clicked!', 'info')}>
                  Outline Button
                </Button>
                <Button variant="ghost" onClick={() => showToast('Ghost button clicked!', 'info')}>
                  Ghost Button
                </Button>
                <Button variant="danger" onClick={() => showToast('Danger action trigger!', 'error')}>
                  Danger Button
                </Button>
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle>Button Sizes & States</CardTitle>
                <CardDescription>Small, medium, large sizes, loading state, disabled states, icons.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" leftIcon={<Zap className="w-3.5 h-3.5" />}>
                    Small with Icon
                  </Button>
                  <Button size="md" rightIcon={<ArrowRight className="w-4 h-4" />}>
                    Medium Next
                  </Button>
                  <Button size="lg" leftIcon={<Check className="w-5 h-5" />}>
                    Large Submit
                  </Button>
                  <Button isLoading size="md">
                    Processing
                  </Button>
                  <Button disabled size="md">
                    Disabled
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle>Icon Buttons & Status Badges</CardTitle>
                <CardDescription>Compact icon controls and categorical indicator badges.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <IconButton icon={<Search className="w-4 h-4" />} aria-label="Search" variant="outline" size="sm" />
                  <IconButton icon={<Zap className="w-4 h-4" />} aria-label="Quick Action" variant="primary" size="md" />
                  <IconButton icon={<AlertCircle className="w-5 h-5" />} aria-label="Alert" variant="danger" size="lg" />
                </div>
                <Divider label="Badges" />
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="default">Default</Badge>
                  <Badge variant="primary" dot>
                    Active Practice
                  </Badge>
                  <Badge variant="success">Passed 92%</Badge>
                  <Badge variant="warning">Needs Review</Badge>
                  <Badge variant="error">2 Incorrect</Badge>
                  <Badge variant="info">Timed Exam</Badge>
                  <Badge variant="neutral">Draft</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 2: FORMS & CONTROLS */}
        {activeTab === 'inputs' && (
          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle>Form Inputs & Validation</CardTitle>
                <CardDescription>Accessible inputs with icons, helper text, error messages, clear buttons.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Search Question Set"
                  placeholder="e.g. General Science Level 1"
                  leftIcon={<Search className="w-4 h-4" />}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onClear={() => setInputText('')}
                  helperText="Type a subject or keyword to filter"
                />

                <Input
                  label="Registered Mobile Number"
                  placeholder="10-digit mobile"
                  value="9876543210"
                  error={inputError}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInputError(val.length < 10 ? 'Mobile number must be at least 10 digits' : '');
                  }}
                />

                <Select
                  label="Mode Selection"
                  value={selectVal}
                  onChange={(e) => setSelectVal(e.target.value)}
                  options={[
                    { label: 'Practice Mode (Untimed)', value: 'practice' },
                    { label: 'Timed Exam Mode', value: 'exam' },
                    { label: 'Rapid Flashcard Revision Mode', value: 'revision' },
                  ]}
                  helperText="Select preferred learning method"
                />
              </CardContent>
            </Card>

            <Card variant="default">
              <CardHeader>
                <CardTitle>Checkboxes & Radio Options</CardTitle>
                <CardDescription>Checkboxes for multiple selections and radio buttons for single option selection.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Checkbox
                  label="Enable Automatic Explanation Reveal"
                  description="Displays immediate answer analysis after selecting an option"
                  checked={checkedState}
                  onChange={(e) => setCheckedState(e.target.checked)}
                />

                <Divider label="Select Difficulty" />

                <div className="space-y-2">
                  <Radio
                    name="difficulty"
                    label="All Difficulties (Recommended)"
                    description="Mix of easy, medium, and hard questions"
                    checked={radioVal === 'option-a'}
                    onChange={() => setRadioVal('option-a')}
                  />
                  <Radio
                    name="difficulty"
                    label="Hard Only"
                    description="Focus on high-challenge questions"
                    checked={radioVal === 'option-b'}
                    onChange={() => setRadioVal('option-b')}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 3: CARDS & CONTAINERS */}
        {activeTab === 'cards' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card variant="default" isInteractive>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="primary">General Knowledge</Badge>
                  <span className="text-xs text-[#5F6368] font-bold">25 Qs</span>
                </div>
                <CardTitle className="mt-2">Indian History & Culture</CardTitle>
                <CardDescription>Practice ancient, medieval, and modern Indian freedom movement topics.</CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={75} showValue label="Set Completion" color="primary" />
              </CardContent>
              <CardFooter>
                <span className="text-xs text-[#5F6368]">Avg Time: 45s / Q</span>
                <Button size="sm" rightIcon={<ArrowRight className="w-3.5 h-3.5" />}>
                  Start Practice
                </Button>
              </CardFooter>
            </Card>

            <Card variant="outlined">
              <CardHeader>
                <CardTitle>Outlined Card Container</CardTitle>
                <CardDescription>Subtle border emphasis for secondary section groupings.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-[#5F6368] leading-relaxed">
                  Clean visual layout without excessive drop shadows or distracting background gradients.
                </p>
              </CardContent>
            </Card>

            <Card variant="flat">
              <CardHeader>
                <CardTitle>Flat Surface Container</CardTitle>
                <CardDescription>Ideal for nested statistical widgets and secondary control blocks.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#5F6368]">Total Attempts</span>
                  <span className="font-extrabold text-[#111111]">14 Passed</span>
                </div>
              </CardContent>
            </Card>

            <Card variant="elevated">
              <CardHeader>
                <CardTitle>Elevated Hero Card</CardTitle>
                <CardDescription>Restrained shadow elevation for primary focal points.</CardDescription>
              </CardHeader>
              <CardContent>
                <Progress value={90} showValue label="Accuracy Gauge" color="success" />
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB 4: MODALS & DRAWERS */}
        {activeTab === 'overlays' && (
          <div className="space-y-6">
            <Card variant="default">
              <CardHeader>
                <CardTitle>Dialog Modals & Slide-over Drawers</CardTitle>
                <CardDescription>Accessible overlay containers with keyboard traps and backdrop blur.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <Button variant="primary" onClick={() => setModalOpen(true)}>
                  Open Sample Modal
                </Button>
                <Button variant="outline" onClick={() => setDrawerOpen(true)}>
                  Open Bottom Drawer
                </Button>
              </CardContent>
            </Card>

            <Modal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              title="Confirm Exam Submission"
              description="Are you sure you want to finish and submit your attempt?"
              footer={
                <>
                  <Button variant="outline" onClick={() => setModalOpen(false)}>
                    Continue Exam
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setModalOpen(false);
                      showToast('Test submitted successfully!', 'success');
                    }}
                  >
                    Submit Now
                  </Button>
                </>
              }
            >
              <div className="space-y-3 text-xs text-[#5F6368]">
                <p>You have answered 18 out of 20 questions.</p>
                <div className="p-3 bg-[#E8FAF9] rounded-xl border border-[#56E8E1]/40 text-[#1b8e88]">
                  <strong>Note:</strong> Unanswered questions will be scored as 0 marks.
                </div>
              </div>
            </Modal>

            <Drawer
              isOpen={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              title="Question Filter Settings"
              footer={
                <Button fullWidth onClick={() => setDrawerOpen(false)}>
                  Apply Filters
                </Button>
              }
            >
              <div className="space-y-4">
                <Select
                  label="Subject Filter"
                  options={[
                    { label: 'All Subjects', value: 'all' },
                    { label: 'General Science', value: 'science' },
                    { label: 'Quantitative Aptitude', value: 'quant' },
                  ]}
                />
                <Checkbox label="Show Unattempted Sets Only" defaultChecked />
              </div>
            </Drawer>
          </div>
        )}

        {/* TAB 5: FEEDBACK & STATES */}
        {activeTab === 'states' && (
          <div className="space-y-6">
            <Alert type="info" title="System Announcement">
              New General Science question bank added with 250+ bilingual questions.
            </Alert>

            <Alert type="success" title="Exam Passed!">
              Congratulations, you scored 92% accuracy in Timed Exam #4.
            </Alert>

            <Alert type="warning" title="Session Warning">
              Your test timer has less than 2 minutes remaining.
            </Alert>

            <Alert type="error" title="Submission Error">
              Network error encountered while saving attempt. Please check your connection.
            </Alert>

            <Divider label="Loading & Skeleton Loaders" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card variant="default">
                <LoadingState message="Fetching question database..." size="md" />
              </Card>

              <Card variant="default" className="space-y-3">
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="card" />
              </Card>
            </div>

            <Divider label="Empty & Error Views" />

            <EmptyState
              title="No Practice Sets Found"
              description="No question sets match your currently selected sector or filter options."
              action={
                <Button size="sm" onClick={() => showToast('Filters reset!')}>
                  Reset Filters
                </Button>
              }
            />

            <ErrorState onRetry={() => showToast('Retrying API request...', 'info')} />
          </div>
        )}
      </div>

      {/* Sample Mobile Bottom Navigation Shell */}
      <ReviseBottomNav activeTab={navTab} onTabChange={setNavTab} />
    </div>
  );
};

export default DesignSystemShowcase;
